import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createSettingsParams,
  type ISettingsParam,
  SettingsParamType,
} from '@wix/tpa-settings';

import { PostImageFill, PostImageRatio } from '../../types';

export type ISettingsParams = {
  aboutSectionTitle: SettingsParamType.Text;
  aboutDescriptionPlaceholder: SettingsParamType.Text;
  aboutEditButtonText: SettingsParamType.Text;
  aboutPrimaryButtonText: SettingsParamType.Text;
  aboutSecondaryButtonText: SettingsParamType.Text;
  badgesSectionTitle: SettingsParamType.Text;
  overviewSectionTitle: SettingsParamType.Text;
  blogPostsTitle: SettingsParamType.Text;
  blogPostsEmptyStateTitle: SettingsParamType.Text;
  headerSectionTitle: SettingsParamType.Text;
  headerSectionSubtitle: SettingsParamType.Text;
  headerSectionButtonText: SettingsParamType.Text;
  emptyStateTitle: SettingsParamType.Text;
  emptyStateText: SettingsParamType.Text;
  showAboutSection: SettingsParamType.Boolean;
  showBadgesSection: SettingsParamType.Boolean;
  showOverviewSection: SettingsParamType.Boolean;
  showBlogPostsSection: SettingsParamType.Boolean;
  postListPageSize: SettingsParamType.Number;
  showBlogPostsCounter: SettingsParamType.Boolean;
  showPostCover: SettingsParamType.Boolean;
  showPostPublishDate: SettingsParamType.Boolean;
  showPostReadingTime: SettingsParamType.Boolean;
  showPostExcerpt: SettingsParamType.Boolean;
  showPostViewsCounter: SettingsParamType.Boolean;
  showPostCommentsCounter: SettingsParamType.Boolean;
  showPostLikesCounter: SettingsParamType.Boolean;
  // TODO: Need to remove usage of this settings param in React code
  // Reference: https://github.com/wix-private/members-area/blob/274b8ae2868eb022c30d52d90741a73d8dc76c09/packages/members-about-ooi/src/components/Profile/Widget/BlogPosts/PostList/Post/Cover/hooks.ts
  postImageFill: SettingsParamType.String;
  postImageRatio: SettingsParamType.Number;
  // TODO: Need to remove usage of this settings param in the Server code
  // Reference: https://github.com/wix-private/members-area/blob/274b8ae2868eb022c30d52d90741a73d8dc76c09/packages/members-about-ooi/src/types/server.ts
  showCommentsStats: SettingsParamType.Boolean;
  showLikesReceivedStats: SettingsParamType.Boolean;
  showTopCommentsStats: SettingsParamType.Boolean;
  showJoinDate: SettingsParamType.Boolean;
  isMobileSectionSettingsEnabled: SettingsParamType.Boolean;
  pageTitleHtmlTag: SettingsParamType.String;
  emptyStateTitleHtmlTag: SettingsParamType.String;
  sectionTitleHtmlTag: SettingsParamType.String;
  badgesSectionTitleHtmlTag: SettingsParamType.String;
  blogPostsTitleHtmlTag: SettingsParamType.String;
};

export type ISettingsParamsWithCustomKeys = {
  aboutSectionTitle: SettingsParamType.Text;
  placeholder: SettingsParamType.Text;
  editAboutButton: SettingsParamType.Text;
  publishAboutButton: SettingsParamType.Text;
  discardAboutButton: SettingsParamType.Text;
  badgesTitle: SettingsParamType.Text;
  overviewTitle: SettingsParamType.Text;
  postListTitle: SettingsParamType.Text;
  postListEmptyStateTitle: SettingsParamType.Text;
  headerTitleText: SettingsParamType.Text;
  headerSubtitleText: SettingsParamType.Text;
  headerButtonText: SettingsParamType.Text;
  emptyStateTitle: SettingsParamType.Text;
  emptyStateText: SettingsParamType.Text;
};

export enum SettingsParamsTextKeys {
  aboutSectionTitle = 'aboutSectionTitle',
  aboutDescriptionPlaceholder = 'placeholder',
  aboutEditButtonText = 'editAboutButton',
  aboutPrimaryButtonText = 'publishAboutButton',
  aboutSecondaryButtonText = 'discardAboutButton',
  badgesSectionTitle = 'badgesTitle',
  overviewSectionTitle = 'overviewTitle',
  blogPostsTitle = 'postListTitle',
  blogPostsEmptyStateTitle = 'postListEmptyStateTitle',
  headerSectionTitle = 'headerTitleText',
  headerSectionSubtitle = 'headerSubtitleText',
  headerSectionButtonText = 'headerButtonText',
  emptyStateTitle = 'emptyStateTitle',
  emptyStateText = 'emptyStateText',
}

export const defaultPostListPageSize = 3;

const textSettingsParam = ({
  defaultValueKey,
  customKey,
}: {
  defaultValueKey: string;
  customKey?: string;
}): Omit<ISettingsParam<SettingsParamType.Text>, 'key'> & { key?: string } => ({
  key: customKey,
  type: SettingsParamType.Text,
  getDefaultValue: ({ t }) => t(defaultValueKey),
  metadata: ({ t }) => ({
    type: DataType.text,
    defaultValue: {
      type: 'static',
      value: t(defaultValueKey),
    },
  }),
});

export default createSettingsParams<ISettingsParams>({
  aboutSectionTitle: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.about-title',
  }),
  aboutDescriptionPlaceholder: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.placeholder-default-text',
    customKey: 'placeholder',
  }),
  aboutEditButtonText: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.edit-button-text',
    customKey: 'editAboutButton',
  }),
  aboutPrimaryButtonText: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.primary-button-text',
    customKey: 'publishAboutButton',
  }),
  aboutSecondaryButtonText: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.secondary-button-text',
    customKey: 'discardAboutButton',
  }),
  badgesSectionTitle: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.badges-title',
    customKey: 'badgesTitle',
  }),
  overviewSectionTitle: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.overview-title',
    customKey: 'overviewTitle',
  }),
  blogPostsTitle: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.posts-title',
    customKey: 'postListTitle',
  }),
  blogPostsEmptyStateTitle: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.no-posts-title',
    customKey: 'postListEmptyStateTitle',
  }),
  headerSectionTitle: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.header-title',
    customKey: 'headerTitleText',
  }),
  headerSectionSubtitle: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.header-subtitle',
    customKey: 'headerSubtitleText',
  }),
  headerSectionButtonText: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.header-button-text',
    customKey: 'headerButtonText',
  }),
  emptyStateTitle: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.empty-state-title',
  }),
  emptyStateText: textSettingsParam({
    defaultValueKey: 'settings.texts-tab.empty-state-text',
  }),
  showAboutSection: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showBadgesSection: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showOverviewSection: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showBlogPostsSection: {
    key: 'showPostListSection',
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showBlogPostsCounter: {
    key: 'showPostListCounter',
    type: SettingsParamType.Boolean,
    getDefaultValue: () => false,
  },
  showJoinDate: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showCommentsStats: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showLikesReceivedStats: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showTopCommentsStats: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  postListPageSize: {
    type: SettingsParamType.Number,
    getDefaultValue: () => defaultPostListPageSize,
    metadata: {
      type: DataType.number,
      defaultValue: {
        type: 'static',
        value: defaultPostListPageSize,
      },
    },
  },
  postImageFill: {
    key: 'postListLayoutResizing',
    type: SettingsParamType.String,
    // TODO: set to PostImageFill.Crop when specs.membersAbout.UseNewPostsCoverDefaults is merged
    getDefaultValue: () => undefined as unknown as PostImageFill, // PostImageFill.Crop,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: PostImageFill.Crop,
      },
    },
  },
  postImageRatio: {
    key: 'postListImageRatio',
    type: SettingsParamType.Number,
    // TODO: set to PostImageRatio.FourToThree when specs.membersAbout.UseNewPostsCoverDefaults is merged
    getDefaultValue: () => undefined as unknown as PostImageRatio, // PostImageRatio.FourToThree,
    metadata: {
      type: DataType.number,
      defaultValue: {
        type: 'static',
        value: PostImageRatio.FourToThree,
      },
    },
  },
  showPostCover: {
    key: 'postListShowCoverImage',
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showPostPublishDate: {
    key: 'postListShowPublishDate',
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showPostReadingTime: {
    key: 'postListShowReadingTime',
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showPostExcerpt: {
    key: 'postListShowDescription',
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showPostViewsCounter: {
    key: 'postListShowViewsCounter',
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showPostCommentsCounter: {
    key: 'postListShowCommentsCounter',
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showPostLikesCounter: {
    key: 'postListShowLikesCounter',
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  isMobileSectionSettingsEnabled: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => false,
  },
  pageTitleHtmlTag: {
    type: SettingsParamType.String,
    getDefaultValue: () => 'h2',
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: 'h2',
      },
    },
  },
  emptyStateTitleHtmlTag: {
    type: SettingsParamType.String,
    getDefaultValue: () => 'h2',
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: 'h2',
      },
    },
  },
  sectionTitleHtmlTag: {
    type: SettingsParamType.String,
    getDefaultValue: () => 'h3',
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: 'h3',
      },
    },
  },
  badgesSectionTitleHtmlTag: {
    type: SettingsParamType.String,
    getDefaultValue: () => 'h3',
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: 'h3',
      },
    },
  },
  blogPostsTitleHtmlTag: {
    type: SettingsParamType.String,
    getDefaultValue: () => 'h3',
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: 'h3',
      },
    },
  },
});
